import { useCurrentAccount } from '@application/api';
import { useRouter, useValidation } from '@application/utils';
import type { BreederReviewsFormValues } from '@presentation/components';
import { BreederReviewList } from '@presentation/components';
import { FormikProvider, useFormik } from 'formik';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { object, string } from 'yup';
import { useReviewList } from './api/useReviewList';
import { useUpdateReview } from './api/useUpdateReview';
import type { DogBreederReviewListControllerProps } from './types';

export const DogBreederReviewListController: FC<DogBreederReviewListControllerProps> = (props) => {
  const { dogBreederId } = props;
  const { requiredMessage, minCharactersMessage } = useValidation();
  const { infoFromCurrentPath } = useRouter();
  const reviewsRef = useRef<HTMLDivElement>(null);

  const { currentAccount } = useCurrentAccount();

  const { reviewList } = useReviewList(dogBreederId);
  const { answerReview, editReviewAnswer, updateDogBreederReviewLoading } = useUpdateReview();
  const [mode, setMode] = useState<'list' | 'edit' | 'create'>('list');
  const [activeReviewId, setActiveReviewId] = useState<number | undefined>();

  const canAnswerReviews = useMemo(() => {
    return dogBreederId === currentAccount?.id;
  }, [currentAccount?.id, dogBreederId]);

  const validationSchema = useMemo(() => {
    return object({
      content: string().required(requiredMessage).min(1, minCharactersMessage(1)),
    });
  }, [minCharactersMessage, requiredMessage]);

  const onSubmitHandler = useCallback(
    (values: BreederReviewsFormValues) => {
      const { content } = values;
      if (activeReviewId && content) {
        switch (mode) {
          case 'create':
            answerReview(activeReviewId, content);
            break;
          case 'edit':
            editReviewAnswer(activeReviewId, content);
            break;
          default:
            break;
        }
      }
      setMode('list');
    },
    [activeReviewId, answerReview, editReviewAnswer, mode],
  );

  const form = useFormik<BreederReviewsFormValues>({
    initialValues: { content: undefined },
    onSubmit: onSubmitHandler,
    validationSchema,
    validateOnChange: true,
    enableReinitialize: true,
  });

  const onEditClickHandler = useCallback(
    (id: number) => {
      setActiveReviewId(id);
      setMode('edit');
      const content = reviewList.items.find((item) => item.id === id)?.answer?.content;
      form.setFieldValue('content', content);
    },
    [form, reviewList.items],
  );
  const onAnswerClickHandler = useCallback(
    (id: number) => {
      setActiveReviewId(id);
      setMode('create');
      form.setFieldValue('content', undefined);
    },
    [form],
  );

  const onCancelClickHandler = useCallback(() => {
    setActiveReviewId(undefined);
    setMode('list');
    form.setFieldValue('content', undefined);
  }, [form]);

  useEffect(() => {
    if (infoFromCurrentPath?.state?.scrollToReviews && reviewsRef) {
      reviewsRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [infoFromCurrentPath.state]);

  return (
    <FormikProvider value={form}>
      <BreederReviewList
        {...reviewList}
        canAnswerReviews={canAnswerReviews}
        onEditClick={onEditClickHandler}
        onAnswerClick={onAnswerClickHandler}
        onCancelClick={onCancelClickHandler}
        updateLoading={updateDogBreederReviewLoading}
        activeReviewId={activeReviewId}
        mode={mode}
        reviewsRef={reviewsRef}
      />
    </FormikProvider>
  );
};
